import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../features/auth/service";
import { parseApiError } from "../lib/http/apiErrors";

const Logo: React.FC = () => (
  <img src="/gullivr_new.png" alt="Gullivr" className="mx-auto mb-4 h-12 w-auto object-contain" />
);

const BtnBlack: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`btn btn-lg w-full bg-black text-white hover:bg-gray-900 active:scale-[0.98] ${className}`}
  >
    {children}
  </button>
);

const ResetPasswordPage: React.FC = () => {
  const navigate         = useNavigate();
  const [params]         = useSearchParams();
  const token            = params.get("token") ?? "";

  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [showPw,    setShowPw]    = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState("");
  const [isLoading, setLoading]   = useState(false);

  if (!token) {
    return (
      <Shell>
        <div className="mb-8 text-center">
          <Logo />
          <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">Invalid link</h1>
        </div>
        <div className="glass-panel p-6 sm:p-8 text-center space-y-4">
          <p className="text-sm text-ink-muted">This reset link is missing or malformed. Request a new one.</p>
          <BtnBlack onClick={() => navigate("/forgot-password")}>Request new link</BtnBlack>
        </div>
      </Shell>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match."); return; }
    if (password.length < 6)  { setError("Password must be at least 8 characters."); return; }
    setError("");
    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(parseApiError(err, "Reset failed. The link may have expired.").message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="mb-8 text-center">
        <Logo />
        <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">
          {done ? "Password reset!" : "Set new password"}
        </h1>
        <p className="mt-2 text-sm text-ink-muted">
          {done
            ? "You can now sign in with your new password."
            : "Choose a strong password for your account."}
        </p>
      </div>

      <div className="glass-panel p-6 sm:p-8">
        {error && <div className="alert-error mb-6">{error}</div>}

        {done ? (
          <div className="space-y-5 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-ink-muted">All existing sessions have been signed out for security.</p>
            <BtnBlack onClick={() => navigate("/login")}>Sign in</BtnBlack>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="label" htmlFor="rp-pw">New password</label>
              <div className="relative">
                <input
                  id="rp-pw"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="input input-lg pr-12 w-full"
                  placeholder="Min 8 characters"
                  autoComplete="new-password"
                  autoFocus
                />
                <EyeToggle show={showPw} onToggle={() => setShowPw((v) => !v)} />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="rp-confirm">Confirm password</label>
              <input
                id="rp-confirm"
                type={showPw ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                className="input input-lg w-full"
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
            </div>

            <BtnBlack type="submit" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="spinner-sm border-white/30 border-t-white" aria-hidden="true" />
                  Resetting…
                </span>
              ) : "Reset password"}
            </BtnBlack>

            <div className="text-center">
              <button type="button" onClick={() => navigate("/login")} className="text-sm font-medium text-ink-muted hover:text-ink">
                ← Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </Shell>
  );
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="page-shell flex min-h-screen items-center justify-center bg-gradient-to-br from-surface-subtle via-surface to-brand-muted/30 px-4 py-10">
    <div className="w-full max-w-sm animate-fade-in-up sm:max-w-md">
      {children}
    </div>
  </div>
);

const EyeToggle: React.FC<{ show: boolean; onToggle: () => void }> = ({ show, onToggle }) => (
  <button type="button" onClick={onToggle} aria-label={show ? "Hide password" : "Show password"}
    className="btn-icon absolute inset-y-0 right-1 my-auto h-8 w-8 border-0 bg-transparent shadow-none">
    {show ? (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.584 10.587a2 2 0 002.828 2.828M9.88 5.09A9.956 9.956 0 0112 4c5 0 9 4 10 8a17.847 17.847 0 01-3.293 4.568M6.228 6.228A17.863 17.863 0 002 12c1 4 5 8 10 8a9.954 9.954 0 004.091-.88" /></svg>
    ) : (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
    )}
  </button>
);

export default ResetPasswordPage;
